import React, { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { Camera, ScanLine, X, AlertCircle, CheckCircle2, Search, Loader2, ShieldAlert } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { orderPartyPrimary, orderPartyContact } from '../lib/orderParty';

/**
 * DriverPickupScanner — scan an invoice barcode at pickup to verify
 * the driver grabbed the right order.
 *
 * Lives at the top of the active-deliveries section on the Driver
 * Dashboard. Why this matters: on busy days drivers stack multiple
 * invoices in the cab and can mix up customers. A 2-second scan
 * before driving away catches mismatches before the wrong package
 * leaves the warehouse.
 *
 * Verification logic (CRITICAL for driver safety):
 *   - If scanned order # is in the driver's active list → SUCCESS
 *     (green banner, scroll to row, highlight, suggest next status).
 *   - If scanned order # is in the driver's history (delivered /
 *     failed) → AMBIGUOUS warning: order already completed.
 *   - If scanned order # is NOT in either list → BIG RED warning:
 *     "Not your order. Do NOT pick up." This is the whole point —
 *     stops wrong-customer deliveries.
 *
 * Camera: prefers rear-facing (`facingMode: 'environment'`) and
 * decodes Code-39, Code-128, QR via @zxing/browser.
 *
 * @param {{
 *   active: Array<{ id:string, order_number:string }>,
 *   history: Array<{ id:string, order_number:string, status:string }>,
 *   onMatch: (order:object) => void,
 * }} props
 */
const DriverPickupScanner = ({ active, history, onMatch }) => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false); // is the scanner sheet visible
  const [value, setValue] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [result, setResult] = useState(null); // { status:'ok'|'wrong'|'completed', order? }
  const videoRef = useRef(null);
  const controlsRef = useRef(null);

  // --- Camera lifecycle ---
  useEffect(() => {
    if (!scanning) return undefined;
    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.QR_CODE,
    ]);
    const reader = new BrowserMultiFormatReader(hints);
    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (res) => {
            if (!res || cancelled) return;
            const text = (res.getText() || '').trim().toUpperCase();
            if (!text) return;
            setValue(text);
            stopScan();
            setTimeout(() => verify(text), 120);
          },
        );
        controlsRef.current = controls;
      } catch (err) {
        if (cancelled) return;
        setScanError(
          err?.name === 'NotAllowedError'
            ? 'Camera blocked. Enable camera permission in settings.'
            : 'Could not start camera. Type the order # instead.',
        );
      }
    })();
    return () => {
      cancelled = true;
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  const stopScan = () => {
    try { controlsRef.current?.stop?.(); } catch { /* ignore */ }
    controlsRef.current = null;
    setScanning(false);
  };

  const close = () => {
    stopScan();
    setOpen(false);
    setValue('');
    setResult(null);
    setScanError(null);
  };

  const verify = (raw) => {
    const num = (raw ?? value).trim().toUpperCase();
    if (!num) {
      toast({ title: 'Enter or scan an order number' });
      return;
    }
    const inActive = active.find((o) => (o.order_number || '').toUpperCase() === num);
    if (inActive) {
      setResult({ status: 'ok', order: inActive });
      // Tiny celebratory vibration on phones that support it.
      try { navigator.vibrate?.(60); } catch { /* ignore */ }
      // Notify the parent so it scrolls + highlights the row.
      onMatch?.(inActive);
      return;
    }
    const inHistory = history.find((o) => (o.order_number || '').toUpperCase() === num);
    if (inHistory) {
      setResult({ status: 'completed', order: inHistory });
      return;
    }
    // Hardest case: warn loud. Long vibration pattern so the driver
    // physically feels something is wrong.
    try { navigator.vibrate?.([200, 80, 200]); } catch { /* ignore */ }
    setResult({ status: 'wrong', orderNumber: num });
  };

  // -------------------------------------------------------------------
  // Pickup-scan trigger pill — collapsed default state.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => { setOpen(true); setResult(null); setValue(''); setScanError(null); }}
        className="w-full rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-4 py-3 mb-3 flex items-center gap-3 transition active:scale-[0.99]"
        data-testid="driver-pickup-scan-trigger"
      >
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
          <ScanLine className="w-5 h-5 text-emerald-300" />
        </div>
        <div className="flex-1 min-w-0 text-left">
          <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-300 uppercase">
            Pickup verification
          </div>
          <div className="text-sm text-white font-semibold leading-tight">
            Scan the invoice barcode to confirm
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5 truncate">
            Catches wrong-customer pickups in 2 seconds
          </div>
        </div>
        <Camera className="w-5 h-5 text-slate-400 flex-shrink-0" />
      </button>
    );
  }

  // -------------------------------------------------------------------
  // Expanded scanner sheet — camera + manual entry + result panel.
  return (
    <div
      className="rounded-2xl border border-white/10 bg-[#111827] p-4 mb-3"
      data-testid="driver-pickup-scanner"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] font-bold tracking-[0.2em] text-emerald-300 uppercase">
            Pickup verification
          </div>
          <h3 className="font-display text-base text-white leading-tight mt-0.5">
            Scan or type the order # on the invoice
          </h3>
        </div>
        <button
          type="button"
          onClick={close}
          className="rounded-full p-1.5 text-slate-400 hover:text-white hover:bg-white/10 transition"
          aria-label="Close scanner"
          data-testid="driver-pickup-scanner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {scanning ? (
        <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3] mb-3">
          <video
            ref={videoRef}
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            muted
          />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-3/4 h-20 border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]" />
          </div>
          <div className="absolute top-2 left-2 right-2 flex items-center justify-between">
            <div className="bg-black/60 backdrop-blur-sm rounded-full px-3 py-1 text-[10px] font-bold text-white inline-flex items-center gap-1.5">
              <ScanLine className="w-3 h-3 animate-pulse" /> Aim at the barcode
            </div>
            <button
              type="button"
              onClick={stopScan}
              className="bg-white/90 hover:bg-white text-slate-900 rounded-full p-1.5 transition"
              aria-label="Stop scanning"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {scanError && (
            <div className="absolute inset-x-3 bottom-3 bg-red-500/95 text-white text-xs font-semibold rounded-lg p-2 flex items-start gap-1.5">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              {scanError}
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2 flex-wrap sm:flex-nowrap mb-3">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
              placeholder="MR-00000077"
              className="w-full rounded-full bg-[#0B1220] border border-white/10 text-white pl-9 pr-4 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500"
              data-testid="driver-pickup-scanner-input"
              autoComplete="off"
              spellCheck="false"
            />
          </div>
          <button
            type="button"
            onClick={() => { setScanError(null); setScanning(true); }}
            className="rounded-full bg-white/5 hover:bg-white/10 text-slate-200 text-xs font-bold px-3 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="driver-pickup-scanner-scan-btn"
            aria-label="Open camera"
          >
            <Camera className="w-4 h-4" /> Scan
          </button>
          <button
            type="button"
            onClick={() => verify()}
            disabled={!value.trim()}
            className="rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-500/30 disabled:cursor-not-allowed text-white text-xs font-bold px-4 py-2 inline-flex items-center gap-1.5 transition"
            data-testid="driver-pickup-scanner-verify-btn"
          >
            Verify
          </button>
        </div>
      )}

      {/* Result banner */}
      {result?.status === 'ok' && (
        <div
          className="rounded-xl border border-emerald-400/40 bg-emerald-500/15 p-3 flex items-start gap-2.5"
          data-testid="driver-pickup-result-ok"
        >
          <CheckCircle2 className="w-5 h-5 text-emerald-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-emerald-100">
              Correct order — go ahead and pick up
            </div>
            <div className="text-[11px] text-emerald-200 mt-0.5">
              {result.order.order_number} · {orderPartyPrimary(result.order)}
              {orderPartyContact(result.order) && (
                <span className="text-neutral-500"> ({orderPartyContact(result.order)})</span>
              )}
            </div>
          </div>
        </div>
      )}
      {result?.status === 'wrong' && (
        <div
          className="rounded-xl border border-red-400/50 bg-red-600/20 p-3 flex items-start gap-2.5"
          data-testid="driver-pickup-result-wrong"
        >
          <ShieldAlert className="w-5 h-5 text-red-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-red-100">
              Wrong order — do NOT pick up
            </div>
            <div className="text-[11px] text-red-200 mt-0.5">
              {result.orderNumber} is not assigned to you. Tell dispatch before driving away.
            </div>
          </div>
        </div>
      )}
      {result?.status === 'completed' && (
        <div
          className="rounded-xl border border-amber-400/40 bg-amber-500/15 p-3 flex items-start gap-2.5"
          data-testid="driver-pickup-result-completed"
        >
          <AlertCircle className="w-5 h-5 text-amber-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-amber-100">
              Already {result.order.status?.replace('_', ' ')} — nothing to pick up
            </div>
            <div className="text-[11px] text-amber-200 mt-0.5">
              {result.order.order_number} · {orderPartyPrimary(result.order)}
              {orderPartyContact(result.order) && (
                <span className="text-neutral-500"> ({orderPartyContact(result.order)})</span>
              )}
            </div>
          </div>
        </div>
      )}

      {!scanning && (
        <p className="text-[10px] text-slate-500 mt-2 leading-snug">
          Scans every invoice barcode (Code-39 / 128 / QR). Camera prefers rear-facing.
        </p>
      )}
    </div>
  );
};

export default DriverPickupScanner;
